// MARK://dhk8/admin-shell/lib/clients/purchase-orders
// Órdenes de compra → dhk8-erp-purchase-orders (incluye el puente cross-entity).
//   GET    /api/erp/purchase-orders?tenantId=...     → { items: ApiPO[], total, page, limit }
//   GET    /api/erp/purchase-orders/:id              → ApiPO (con lines + receipts)
//   POST   /api/erp/purchase-orders  body { tenantId, supplierId, networkId?, notes?, lines[] }
//   POST   /api/erp/purchase-orders/:id/submit       → DRAFT → SUBMITTED
//   POST   /api/erp/purchase-orders/:id/approve  body { approvedBy }   → SUBMITTED → APPROVED
//   POST   /api/erp/purchase-orders/:id/receive  body { receivedCents, notes? } → APPROVED|PARTIAL → PARTIAL|RECEIVED
//   POST   /api/erp/purchase-orders/:id/cancel   body { reason? }      → → CANCELLED
//   GET    /api/erp/purchase-orders/network/inbox?tenantId=...  → bandeja del proveedor
//   POST   /api/erp/purchase-orders/network/authorize  body { inboxItemId, poId, networkId?, approvedBy }
//
// CONTRACT NOTE: rutas bajo `/api/erp/...` (NO `/v1/...`). El ingress mapea
// `/v1/dhk8-erp-purchase-orders/(.*)` → `/(.*)` en el pod. Respuestas envueltas
// en `{ ok, data }` → apiData. Submit de una OC con networkId enlazado dispara
// el puente: la solicitud aterriza en la bandeja del tenant proveedor.
import { svcUrl, apiData, requireTenant } from '../api.js'
import { PREVIEW_STUB, mock } from '../env.js'
import { authTenantId } from '../../stores/auth-store.js'
import { gqlClient } from '../gql.js'
import { AdminPurchaseOrdersQuery, AdminPurchaseOrderQuery, AdminCreatePurchaseOrderMutation, AdminUpdatePurchaseOrderMutation, AdminReceivePurchaseOrderMutation, AdminCancelPurchaseOrderMutation, AdminSubmitPurchaseOrderMutation, AdminApprovePurchaseOrderMutation } from '../queries/purchase-orders.queries.js'
import { AdminProductsQuery } from '../queries/catalog.queries.js'

const PO_SVC = 'dhk8-erp-purchase-orders'

export type POStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'PARTIAL' | 'RECEIVED' | 'CANCELLED'

export interface POLine {
  id: string
  poId: string
  productId: string
  description: string | null
  qty: string | number
  uom: string
  unitCents: number
  totalCents: number
  receivedQty: string | number
}

export interface POReceipt {
  id: string
  poId: string
  receivedCents: number
  balanceCents: number
  notes: string | null
  receivedAt: string
}

export interface PurchaseOrder {
  id: string
  tenantId: string
  networkId: string | null
  supplierId: string
  status: POStatus
  totalCents: number
  notes: string | null
  archived: boolean
  createdAt: string
  updatedAt: string
  lines: POLine[]
  receipts?: POReceipt[]
}


export interface NewPOLine {
  productId: string
  description?: string
  qty: number
  uom: string
  unitCents: number
}
export interface CreatePOInput {
  supplierId: string
  networkId?: string
  notes?: string
  lines: NewPOLine[]
}

// Edición de una OC en borrador (DRAFT). `lines` reemplaza el set completo.
// Todo es opcional: solo se manda lo que cambió.
export interface UpdatePOInput {
  supplierId?: string
  notes?: string | null
  lines?: NewPOLine[]
}

// Bandeja del proveedor: filas de dhk8_network.NetworkInboxItem.
export interface InboxItem {
  id: string
  tenantId: string
  status: string
  payload: {
    kind?: string
    networkId?: string
    poId?: string
    fromTenantId?: string
    toTenantId?: string
    supplierId?: string
    lines?: Array<{ productId: string; qty: number; unitCents: number; totalCents: number }>
    totalCents?: number
    submittedAt?: string
  }
  createdAt: string
  updatedAt: string
}

export const PO_STATUS_LABEL: Record<POStatus, string> = {
  DRAFT: 'Borrador',
  SUBMITTED: 'Enviada',
  APPROVED: 'Aprobada',
  PARTIAL: 'Parcial',
  RECEIVED: 'Recibida',
  CANCELLED: 'Cancelada',
}
export const PO_STATUS_BADGE: Record<POStatus, string> = {
  DRAFT: 'adm-badge--info',
  SUBMITTED: 'adm-badge--warn',
  APPROVED: 'adm-badge--ok',
  PARTIAL: 'adm-badge--warn',
  RECEIVED: 'adm-badge--ok',
  CANCELLED: 'adm-badge--danger',
}

export function lineQty(l: POLine): number { return typeof l.qty === 'number' ? l.qty : Number(l.qty) }
export function lineReceived(l: POLine): number { return typeof l.receivedQty === 'number' ? l.receivedQty : Number(l.receivedQty) }

// ── preview store ──────────────────────────────────────────────────────────
const db: { pos: PurchaseOrder[] } = { pos: [] }
const tq = () => `tenantId=${encodeURIComponent(authTenantId())}`
const newId = () => `po-${Math.random().toString(36).slice(2, 10)}`

// Mapea la PurchaseOrder del grafo (typed-document-node) al tipo de UI. qty/receivedQty
// llegan como String (Decimal-safe); el UI los acepta como string. receipts solo en detalle.
interface GqlPOLine { id: string; poId: string; productId: string; description?: string | null; qty: string; uom: string; unitCents: number; totalCents: number; receivedQty: string }
interface GqlPO {
  id: string; tenantId: string; networkId?: string | null; supplierId: string; status: string
  totalCents: number; notes?: string | null; archived: boolean; createdAt: string; updatedAt: string
  lines: ReadonlyArray<GqlPOLine>
  receipts?: ReadonlyArray<{ id: string; poId: string; receivedCents: number; balanceCents: number; notes?: string | null; receivedAt: string }>
}
function toUiPO(p: GqlPO): PurchaseOrder {
  const po: PurchaseOrder = {
    id: p.id, tenantId: p.tenantId, networkId: p.networkId ?? null, supplierId: p.supplierId,
    status: p.status as POStatus, totalCents: p.totalCents, notes: p.notes ?? null, archived: p.archived,
    createdAt: p.createdAt, updatedAt: p.updatedAt,
    lines: p.lines.map((l) => ({
      id: l.id, poId: l.poId, productId: l.productId, description: l.description ?? null,
      qty: l.qty, uom: l.uom, unitCents: l.unitCents, totalCents: l.totalCents, receivedQty: l.receivedQty,
    })),
  }
  if (p.receipts) {
    po.receipts = p.receipts.map((r) => ({
      id: r.id, poId: r.poId, receivedCents: r.receivedCents, balanceCents: r.balanceCents,
      notes: r.notes ?? null, receivedAt: r.receivedAt,
    }))
  }
  return po
}

export async function listPurchaseOrders(signal?: AbortSignal): Promise<PurchaseOrder[]> {
  if (PREVIEW_STUB) return mock(db.pos)
  if (requireTenant() === null) return []
  // F2.3 / ADR-0009: lectura por el grafo (tenant del JWT, RLS). Escrituras siguen REST.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminPurchaseOrdersQuery, { limit: 100 }, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.purchaseOrders.items ?? []).map(toUiPO)
}

export async function getPurchaseOrder(id: string, signal?: AbortSignal): Promise<PurchaseOrder> {
  if (PREVIEW_STUB) {
    const po = db.pos.find((x) => x.id === id)
    if (!po) throw new Error('Orden de compra no encontrada')
    return mock(po)
  }
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminPurchaseOrderQuery, { id }, ctx).toPromise()
  if (res.error) throw res.error
  const po = res.data?.purchaseOrder
  if (!po) throw new Error('Orden de compra no encontrada')
  return toUiPO(po)
}

export async function createPurchaseOrder(input: CreatePOInput): Promise<{ id: string }> {
  const lines = input.lines.map((l) => ({
    productId: l.productId,
    ...(l.description ? { description: l.description } : {}),
    qty: l.qty,
    uom: l.uom,
    unitCents: l.unitCents,
    totalCents: Math.round(l.qty * l.unitCents),
  }))
  if (PREVIEW_STUB) {
    const id = newId()
    const totalCents = lines.reduce((s, l) => s + l.totalCents, 0)
    db.pos.unshift({
      id, tenantId: 'tnt_demo', networkId: input.networkId ?? null, supplierId: input.supplierId,
      status: 'DRAFT', totalCents, notes: input.notes ?? null, archived: false,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
      lines: lines.map((l) => ({ id: `pol-${Math.random().toString(36).slice(2, 8)}`, poId: id, productId: l.productId, description: l.description ?? null, qty: l.qty, uom: l.uom, unitCents: l.unitCents, totalCents: l.totalCents, receivedQty: 0 })),
    })
    return mock({ id })
  }
  // F8.x / ADR-0009: alta por el grafo. tenantId lo pone el JWT (no va en el input).
  const gqlInput: { supplierId: string; networkId?: string; notes?: string; lines: typeof lines } = { supplierId: input.supplierId, lines }
  if (input.networkId) gqlInput.networkId = input.networkId
  if (input.notes) gqlInput.notes = input.notes
  const res = await gqlClient.mutation(AdminCreatePurchaseOrderMutation, { input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const po = res.data?.createPurchaseOrder
  if (!po) throw new Error('No se pudo crear la orden de compra')
  return { id: po.id }
}

export async function updatePurchaseOrder(id: string, input: UpdatePOInput): Promise<PurchaseOrder> {
  // Las líneas (si se mandan) reemplazan el set completo; totalCents se deriva por línea.
  const lines = input.lines?.map((l) => ({
    productId: l.productId,
    ...(l.description ? { description: l.description } : {}),
    qty: l.qty,
    uom: l.uom,
    unitCents: l.unitCents,
    totalCents: Math.round(l.qty * l.unitCents),
  }))
  if (PREVIEW_STUB) {
    const po = db.pos.find((x) => x.id === id)
    if (!po) throw new Error('Orden de compra no encontrada')
    if (input.supplierId !== undefined) po.supplierId = input.supplierId
    if (input.notes !== undefined) po.notes = input.notes
    if (lines) {
      po.lines = lines.map((l) => ({ id: `pol-${Math.random().toString(36).slice(2, 8)}`, poId: id, productId: l.productId, description: l.description ?? null, qty: l.qty, uom: l.uom, unitCents: l.unitCents, totalCents: l.totalCents, receivedQty: 0 }))
      po.totalCents = lines.reduce((s, l) => s + l.totalCents, 0)
    }
    po.updatedAt = new Date().toISOString()
    return mock(po)
  }
  // F8.x / ADR-0009: edición por el grafo (tenant del JWT, IDOR-guard por RLS). Solo DRAFT.
  const gqlInput: { supplierId?: string; notes?: string; lines?: typeof lines } = {}
  if (input.supplierId !== undefined) gqlInput.supplierId = input.supplierId
  if (input.notes !== undefined && input.notes !== null) gqlInput.notes = input.notes
  if (lines) gqlInput.lines = lines
  const res = await gqlClient.mutation(AdminUpdatePurchaseOrderMutation, { id, input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const po = res.data?.updatePurchaseOrder
  if (!po) throw new Error('No se pudo actualizar la orden de compra')
  // La mutation devuelve un subconjunto (sin tenantId/networkId/archived/timestamps);
  // re-leemos para mantener el tipo de UI completo y consistente con el detalle.
  return getPurchaseOrder(id)
}

function previewSetStatus(id: string, status: POStatus): void {
  const po = db.pos.find((x) => x.id === id)
  if (po) { po.status = status; po.updatedAt = new Date().toISOString() }
}

export async function submitPurchaseOrder(id: string): Promise<void> {
  if (PREVIEW_STUB) { previewSetStatus(id, 'SUBMITTED'); await mock(undefined); return }
  // F6.2 R3c / ADR-0008: submit por el grafo (tenant del JWT, IDOR-guard por RLS).
  const res = await gqlClient.mutation(AdminSubmitPurchaseOrderMutation, { id }).toPromise()
  if (res.error) throw res.error
}

export async function approvePurchaseOrder(id: string, approvedBy: string): Promise<void> {
  if (PREVIEW_STUB) { previewSetStatus(id, 'APPROVED'); await mock(undefined); return }
  // F6.2 R3c / ADR-0008: approve por el grafo (tenant del JWT, IDOR-guard por RLS).
  const res = await gqlClient.mutation(AdminApprovePurchaseOrderMutation, { id, approvedBy }).toPromise()
  if (res.error) throw res.error
}

export async function receivePurchaseOrder(id: string, receivedCents: number, notes?: string): Promise<void> {
  if (PREVIEW_STUB) { previewSetStatus(id, 'RECEIVED'); await mock(undefined); return }
  // F8.x / ADR-0009: recepción por el grafo (tenant del JWT, IDOR-guard por RLS).
  const res = await gqlClient.mutation(AdminReceivePurchaseOrderMutation, { id, receivedCents, notes: notes ?? null }).toPromise()
  if (res.error) throw res.error
}

export async function cancelPurchaseOrder(id: string, reason?: string): Promise<void> {
  if (PREVIEW_STUB) { previewSetStatus(id, 'CANCELLED'); await mock(undefined); return }
  // F8.x / ADR-0009: cancelación por el grafo (tenant del JWT, IDOR-guard por RLS).
  const res = await gqlClient.mutation(AdminCancelPurchaseOrderMutation, { id, reason: reason ?? null }).toPromise()
  if (res.error) throw res.error
}

// ── catálogo comprable ──────────────────────────────────────────────────────
// Una OC suele pedir INSUMOS (café en grano, vasos), no sólo productos
// vendibles. El catalog client filtra a type=PRODUCT — aquí pedimos TODO el
// catálogo del tenant (incluye INGREDIENT) para poblar el selector de líneas.
export interface PurchasableProduct { id: string; name: string; sku: string | null; type: string; unit: string }


export async function listPurchasableProducts(signal?: AbortSignal): Promise<PurchasableProduct[]> {
  if (PREVIEW_STUB) return mock([])
  // F6.2 R1 / ADR-0008: catálogo comprable por el grafo (erp-products `products`,
  // tenant del JWT, RLS, paginado). Sin `type` trae TODO (PRODUCT + INGREDIENT)
  // — el café en grano (INGREDIENT) es comprable. MODIFICADOR se filtra
  // client-side. Sustituye GET REST /v1/products.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const out: PurchasableProduct[] = []
  for (let page = 1; page < 100; page++) {
    const res = await gqlClient.query(AdminProductsQuery, { page, limit: 100 }, ctx).toPromise()
    if (res.error) throw res.error
    const pageData = res.data?.products
    const items = pageData?.items ?? []
    for (const p of items) {
      if ((p.tags ?? []).includes('MODIFICADOR')) continue
      out.push({ id: p.id, name: p.name, sku: p.sku, type: p.type, unit: p.unit ?? 'UNIT' })
    }
    const total = pageData?.total ?? items.length
    if (items.length < 100 || page * 100 >= total) break
  }
  return out
}

// ── bandeja del proveedor (puente cross-entity) ─────────────────────────────
export async function listNetworkInbox(signal?: AbortSignal): Promise<InboxItem[]> {
  if (PREVIEW_STUB) return mock([])
  const res = await apiData<{ items: InboxItem[] }>(svcUrl(PO_SVC, `/api/erp/purchase-orders/network/inbox?${tq()}`), { signal })
  return res.items
}

export async function authorizeNetworkRequest(input: { inboxItemId: string; poId: string; networkId?: string; approvedBy: string }): Promise<void> {
  if (PREVIEW_STUB) return mock(undefined)
  const body: Record<string, unknown> = { inboxItemId: input.inboxItemId, poId: input.poId, approvedBy: input.approvedBy }
  if (input.networkId) body['networkId'] = input.networkId
  await apiData<unknown>(svcUrl(PO_SVC, '/api/erp/purchase-orders/network/authorize'), { method: 'POST', body })
}
